import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

const StickyNotesPopup = ({ isOpen, onClose, onSave, initialValue, position }) => {
  const [value, setValue] = useState(initialValue || '');
  const [adjustedPos, setAdjustedPos] = useState({ top: 0, left: 0 });
  const [arrowPos, setArrowPos] = useState({ top: '-8px', left: '20px' });
  const [arrowDirection, setArrowDirection] = useState('down'); // 'down' or 'up'
  const textareaRef = useRef(null);
  const popupRef = useRef(null);

  // Reset value when popup opens or when initialValue changes
  useEffect(() => {
    if (isOpen) {
      setValue(initialValue || '');
    }
  }, [isOpen, initialValue]);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => {
        textareaRef.current?.focus();
        const len = value.length;
        textareaRef.current?.setSelectionRange(len, len);
      }, 50);
    }
  }, [isOpen, value.length]);

  // Calculate and adjust position when popup opens
  useLayoutEffect(() => {
    if (isOpen && position) {
      const popupWidth = 320;
      const popupHeight = 150; // Reduced height to match actual content
      const gap = 8;
      const arrowHeight = 8;
      
      let top = position.top;
      let left = position.left;
      let direction = 'down';
      
      // Adjust if popup would go off right edge
      // eslint-disable-next-line no-undef
      if (left + popupWidth > globalThis.innerWidth - 10) {
        // eslint-disable-next-line no-undef
        left = globalThis.innerWidth - popupWidth - 10;
      }
      
      // Adjust if popup would go off left edge
      if (left < 10) {
        left = 10;
      }
      
      // If popup would go off bottom, show it above the cell
      // eslint-disable-next-line no-undef
      if (top + popupHeight + arrowHeight > globalThis.innerHeight - 10) {
        top = position.top - popupHeight - gap - 36; // 36 is approx cell height
        direction = 'up';
      }
      
      // If still off top, just position at top
      if (top < 10) {
        top = 10;
        direction = 'down';
      }
      
      setAdjustedPos({ top, left });
      setArrowDirection(direction);
      
      // Calculate arrow position relative to the popup's left edge
      const arrowLeftRelative = position.left - left + 8;
      
      // Calculate arrow left position
      const arrowLeft = `${Math.max(9, Math.min(arrowLeftRelative, popupWidth - 18))}px`;
      
      setArrowPos({
        left: arrowLeft
      });
    }
  }, [isOpen, position]);

  const handleSave = () => {
    if (value !== initialValue) {
      onSave(value);
    }
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      handleSave();
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        if (value !== initialValue) {
          onSave(value);
        }
        onClose();
      }
    };

    if (isOpen) {
      // Small delay to prevent immediate close from the click that opened it
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, value, initialValue, onSave, onClose]);

  if (!isOpen || !position) return null;

  const popupContent = (
    <div
      ref={popupRef}
      role="region"
      aria-label="Sticky note editor"
      tabIndex={0}
      style={{
        position: 'fixed',
        top: `${adjustedPos.top}px`,
        left: `${adjustedPos.left}px`,
        width: '320px',
        backgroundColor: '#ffffff',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        zIndex: 999999,
        display: 'flex',
        flexDirection: 'column',
        animation: 'popupSlideIn 0.15s ease-out',
        overflow: 'visible',
        outline: 'none',
      }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      {/* Arrow pointer - up (points to cell below) */}
      {arrowDirection === 'down' && (
        <>
          {/* Border triangle */}
          <div
            style={{
              position: 'absolute',
              top: '-9px',
              left: arrowPos.left,
              width: '0',
              height: '0',
              borderLeft: '9px solid transparent',
              borderRight: '9px solid transparent',
              borderBottom: '9px solid #d1d5db',
              zIndex: 1,
            }}
          />
          {/* White fill triangle */}
          <div
            style={{
              position: 'absolute',
              top: '-7px',
              left: `calc(${arrowPos.left} + 1px)`,
              width: '0',
              height: '0',
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderBottom: '8px solid #ffffff',
              zIndex: 2,
            }}
          />
        </>
      )}

      {/* Arrow pointer - down (points to cell above) */}
      {arrowDirection === 'up' && (
        <>
          {/* Border triangle */}
          <div
            style={{
              position: 'absolute',
              bottom: '-9px',
              left: arrowPos.left,
              width: '0',
              height: '0',
              borderLeft: '9px solid transparent',
              borderRight: '9px solid transparent',
              borderTop: '9px solid #d1d5db',
              zIndex: 1,
            }}
          />
          {/* White fill triangle */}
          <div
            style={{
              position: 'absolute',
              bottom: '-7px',
              left: `calc(${arrowPos.left} + 1px)`,
              width: '0',
              height: '0',
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: '8px solid #ffffff',
              zIndex: 2,
            }}
          />
        </>
      )}

      {/* Content */}
      <div style={{ padding: '8px' }}>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter transformation rule..."
          style={{
            width: '100%',
            height: '100px',
            padding: '6px',
            fontSize: '13px',
            lineHeight: '1.4',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            resize: 'none',
            backgroundColor: '#ffffff',
            color: '#1f2937',
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#3b82f6';
            e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.2)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#d1d5db';
            e.target.style.boxShadow = 'none';
          }}
        />
        <div
          style={{
            marginTop: '8px',
            fontSize: '11px',
            color: '#6b7280',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>Esc to cancel • Ctrl+Enter to save</span>
          <button
            onClick={handleSave}
            type="button"
            style={{
              padding: '4px 12px',
              fontSize: '12px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 500,
              transition: 'background-color 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
            onFocus={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
            onBlur={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
          >
            Save
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes popupSlideIn {
            from {
              opacity: 0;
              transform: translateY(-8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );

  // Render using portal to document.body to avoid z-index issues
  return ReactDOM.createPortal(popupContent, document.body);
};

StickyNotesPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialValue: PropTypes.string,
  position: PropTypes.shape({
    top: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired,
  }).isRequired,
};

export default StickyNotesPopup;